import React, { useState, useEffect, useContext } from 'react';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { getRKeeperCredentialsSetup } from 'api/restaurant';
import { RestaurantIntegrationRKeeper } from './_form_integration_rkeeper';
import { RestaurantIntegrationIiko } from './_form_integration_iiko';

export const RestaurantIntegration = ({ restaurant }) => {
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const [setupData, setSetupData] = useState(null);
  const [loadingSetup, setLoadingSetup] = useState(true);

  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const hasAnyIntegration = (!!restaurant.iiko_code || !!restaurant.rkeeper_code);

  const getIntegration = async (restaurantId) => {
    if (setupData) {
      return;
    }

    setLoadingSetup(true);

    const response = await getRKeeperCredentialsSetup({
      alertController,
      restaurantId,
      token: auth.token,
      companyId: auth.company?.company,
    });

    setSetupData(response);
    setLoadingSetup(false);
  };

  useEffect(() => {
    if (restaurant && hasAnyIntegration) {
      setSelectedIntegration(restaurant.iiko_code ? 'iiko' : 'rkeeper');

      getIntegration(restaurant._id);
    } else if (restaurant && !hasAnyIntegration) {
      setLoadingSetup(false);
    }
  }, [restaurant]);

  const renderIntegrationSelection = () => {
    return (
      <div className="grid grid-cols-6 gap-6">
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="integration_type" className="block text-sm font-medium text-gray-700">
            Integration
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <DropDownList
              id="integration_type"
              options={[
                { value: 'rkeeper', label: 'RKeeper' },
                { value: 'iiko', label: 'Iiko' },
              ]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              onChange={(e) => { setSelectedIntegration(e.target.value); }}
              defaultValue={selectedIntegration}
              disabled
            />
          </div>
        </div>
      </div>
    );
  };

  const onCurrentIntegrationChanged = (name, data) => {
    if (name === 'rkeeper') {
      setSetupData({
        ...(setupData || {}),
        currencyCode: data.currency_code,
        restaurantCode: data.restaurant_code,
        prepaymentReasonCode: data.prepayment_reason_code,
      });
    } else if (name === 'iiko') {
      setSetupData({
        ...(setupData || {}),
        organizationId: data.organization_id,
        paymentId: data.payment_id,
        menuId: data.menu_id,
        priceCategoryId: data.price_category_id,
        terminalGroup: data.terminal_group,
        useDeliveryApi: data.use_delivery_api,
        takeoutOrderType: data.takeout_order_type,
        deliveryOrderType: data.delivery_order_type,
      });
    }
  };

  return (
    <div className="shadow sm:rounded-md sm:overflow-hidden">
      <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Integration
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Setup integration to import data
          </p>
        </div>
        {selectedIntegration === 'rkeeper' && (
          <RestaurantIntegrationRKeeper
            restaurant={restaurant}
            setupData={setupData}
            loading={loadingSetup}
            integrationSelectionBlock={renderIntegrationSelection()}
            onSaved={onCurrentIntegrationChanged}
          />
        )}
        {selectedIntegration === 'iiko' && (
          <RestaurantIntegrationIiko
            restaurant={restaurant}
            setupData={setupData}
            loading={loadingSetup}
            integrationSelectionBlock={renderIntegrationSelection()}
            onSaved={onCurrentIntegrationChanged}
          />
        )}
        {!hasAnyIntegration && (
        <p className=" text-sm text-gray-700">
          Integration not connected
        </p>
        )}
      </div>
    </div>
  );
};
